import axios from 'axios';
import { useLanguage } from '../i18n';
import { useAuthStore } from '../store';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000').replace(/\/$/, '');
export const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  config.authPortal = useAuthStore.getState().activePortal;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});


api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && error.config?.headers?.Authorization && !error.config?.url?.startsWith('/auth/login')) {
      const portal = error.config.authPortal;
      if (`Bearer ${useAuthStore.getState().sessions[portal]?.token}` === error.config.headers.Authorization) {
        useAuthStore.getState().logout(portal);
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password, portal) => api.post('/auth/login', { email, password, portal }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  acceptInvite: (token, password) => api.post('/auth/accept-invite', { token, password }),
  getInviteInfo: (token) => api.get(`/auth/invite-info/${token}`)
};

// Public APIs (no auth needed)
export const publicAPI = {
  resolveTable: (establishmentId, tableNumber) =>
    api.get(`/public/establishment/${establishmentId}/table-by-number/${tableNumber}`),

  getTableByNumber: (establishmentId, tableNumber) =>
    api.get(`/public/establishment/${establishmentId}/table-by-number/${tableNumber}`),

  getTable: (tableId) => api.get(`/public/table/${tableId}`),
  getMenu: (establishmentId) => api.get(`/public/menu/${establishmentId}`),
  getEstablishment: (establishmentId) => api.get(`/public/establishment/${establishmentId}`),
  getAvailability: (establishmentId) => api.get(`/public/establishment/${establishmentId}/availability`),
  getSuggestions: (establishmentId) => api.get(`/public/suggestions/${establishmentId}`),
  getActiveHappyHours: (establishmentId) => api.get(`/public/happy-hours/${establishmentId}`),
  createOrder: (data) => api.post('/orders', data),
  getOrder: (orderId) => api.get(`/orders/${orderId}`),
  trackEvent: (data) => api.post('/analytics/track', data),
  applyPromoCode: (code, subtotal, establishmentId) =>
    api.post('/public/apply-promo', {
      code,
      subtotal,
      establishment_id: establishmentId
    })
};

// Tables APIs
export const tablesAPI = {
  getAll: () => api.get('/tables'),
  create: (data) => api.post('/tables', data),
  createBulk: (data) => api.post('/tables/bulk', data),
  delete: (id) => api.delete(`/tables/${id}`),
  deleteBulk: (data) => api.delete('/tables/bulk', { data }),
  getQR: (id) => api.get(`/tables/${id}/qr`),
  getEstablishmentQR: () => api.get('/establishment/qr')
};

// Categories APIs
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`)
};

// Products APIs
export const productsAPI = {
  setAvailability: (id, available) => api.patch(`/products/${id}/availability`, { available }),
  getAll: () => api.get('/products'),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/products/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`)
};

// Orders APIs
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getActive: () => api.get('/orders/active'),
  updateStatus: (id, status, reason = '') => api.put(`/orders/${id}/status`, { status, reason }),
  undoStatus: (id, token) => api.post(`/orders/${id}/undo-status`, { token }),
  get: (id) => api.get(`/orders/${id}`)
};

// Staff APIs
export const staffAPI = {
  getAll: () => api.get('/staff'),
  create: (data) => api.post('/staff', data),
  delete: (id) => api.delete(`/staff/${id}`),
  getInvites: () => api.get('/staff/invites')
};

// Superadmin APIs
export const superadminAPI = {
  getEstablishments: () => api.get('/superadmin/establishments'),
  createEstablishment: (data) => api.post('/superadmin/establishments', data),
  deleteEstablishment: (id) => api.delete(`/superadmin/establishments/${id}`)
};

// Analytics APIs
export const analyticsAPI = {
  getJourney: () => api.get('/analytics/journey'),
  getRevenue: () => api.get('/analytics/revenue'),
  getOrders: () => api.get('/analytics/orders'),
  getProducts: () => api.get('/analytics/products'),
  getPeakHours: () => api.get('/analytics/peak-hours'),
  getTips: () => api.get('/analytics/tips')
};

// Establishment APIs
export const establishmentAPI = {
  get: () => api.get('/establishment'),
  getEntryQR: () => api.get('/establishment/entry-qr'),
  update: (data) => api.put('/establishment', data)
};

// Happy Hour APIs
export const happyHourAPI = {
  getAll: () => api.get('/happy-hours'),
  create: (data) => api.post('/happy-hours', data),
  update: (id, data) => api.put(`/happy-hours/${id}`, data),
  delete: (id) => api.delete(`/happy-hours/${id}`)
};

// Promo Code APIs
export const promoCodeAPI = {
  getAll: () => api.get('/promo-codes'),
  create: (data) => api.post('/promo-codes', data),
  update: (id, data) => api.put(`/promo-codes/${id}`, data),
  delete: (id) => api.delete(`/promo-codes/${id}`)
};

// Payment APIs
export const paymentAPI = {
  createCheckout: (orderId, originUrl) => api.post('/payments/checkout', { order_id: orderId, origin_url: originUrl, locale: useLanguage.getState().language }),
  createTipCheckout: (orderId, amount, originUrl) => api.post('/payments/tip', { order_id: orderId, amount, origin_url: originUrl, locale: useLanguage.getState().language }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  createPaymentIntent: (orderId) => api.post('/payments/create-intent', { order_id: orderId }),
  createTipIntent: (orderId, amount) => api.post('/payments/create-tip-intent', { order_id: orderId, amount }),
  confirmIntent: (paymentIntentId) => api.post(`/payments/confirm-intent/${paymentIntentId}`)
};

// Notification APIs
export const notificationAPI = {
  subscribe: (orderId, endpoint, keys) => api.post('/notifications/subscribe', { order_id: orderId, endpoint, keys }),
  unsubscribe: (orderId) => api.delete(`/notifications/unsubscribe/${orderId}`)
};

// Subscription APIs
export const subscriptionAPI = {
  getPlans: () => api.get('/subscription/plans'),
  getStatus: () => api.get('/subscription/status'),
  createCheckout: (planId, originUrl) => api.post('/subscription/checkout', { plan_id: planId, origin_url: originUrl }),
  verify: (sessionId) => api.get(`/subscription/verify/${sessionId}`)
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed')
};

// WebSocket URL
export const getWSUrl = (establishmentId) => {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const backendHost = BACKEND_URL.replace(/^https?:\/\//, '');
  return `${wsProtocol}//${backendHost}/ws/orders/${establishmentId}`;
};

export default api;
